import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { addClickHandlers } from 'tabview/graphComponent/graphEvents';
import { GraphEventType } from 'tabview/graphComponent/graphEvents/types';
import { updateNodesAndEdgesConnectednessToSelection } from 'yfilesExtensions/graphSelection';
import { runLayout } from 'tabview/graphComponent/runLayout';
import { NOOP_VIEW_STATUS_REPORTER } from 'tabview/activeViewsStatus/viewStatusReporter';
import { dispatchAction } from '@ardoq/rxbeach';
import { notifyViewLoading } from 'tabview/actions';
import {
  applySelectionDecorators,
  createGraphComponent,
  focusableItems,
  selectableItems as defaultSelectableItems,
} from 'tabview/graphComponent/yfilesHelper';
import { FastGraphModelManager } from 'yfilesExtensions/FastGraphModelManager';
import { initializeHighlightIndicator } from 'yfilesExtensions/view/highlightIndicator/initializeHighlightIndicator';
import { ViewIds } from '@ardoq/api-types';
import {
  Cursor,
  type GraphComponent,
  GraphInputMode,
  GraphViewerInputMode,
  Command,
  LayoutDescriptor,
  type ViewportLimiter,
} from '@ardoq/yfiles';
import type { ModernizedBlockDiagramViewCoreProps } from './types';
import { onLayoutGraph as onLayoutGraphHandler } from './view/yFilesExtensions/onLayoutGraph';
import { configureLayout } from './view/yFilesExtensions/modernized/configureLayout';
import { t } from '@ardoq/i18n';
import { YGraphBuilder as ModernizedYGraphBuilder } from './view/yFilesExtensions/modernized/graphBuilder/YGraphBuilder';
import { FillingDiv, GraphContainer } from 'tabview/graphComponent/atoms';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import {
  register as registerCanvasHitTest,
  unregister as unregisterCanvasHitTest,
} from 'tabview/canvasRendering/canvasHitTestRegistry';
import { useToolTipParentElement } from 'tabview/graphComponent/useToolTipParentElement';
import { toClientModelHitTest } from 'yfilesExtensions/view/toClientModelHitTest';
import { toClientNodeLocator } from 'yfilesExtensions/view/toClientNodeLocator';
import {
  ComponentGraphGroup,
  ComponentGraphNode,
  getAddedAndUpdatedItems,
  getGraphNodeCount,
  GraphEdgeBase,
  GraphViewDataModel,
} from '@ardoq/graph';
import type { OnLayoutGraphArgs } from 'tabview/graphComponent/onLayoutGraph';
import {
  clearSubscriptions,
  subscribeToAction,
} from 'streams/utils/streamUtils';
import {
  canvasResolvedImageReady,
  svgImageReady,
} from 'tabview/canvasRendering/canvasResolvedImages';
import { ModernizedViewportLimiter } from './ModernizedViewportLimiter';
import { noop } from 'lodash';
import { isFreshLayout, isSelectionStaleAfterNodeUpdate } from './utils';
import { clearGraphSelection } from '@ardoq/component-selection';
import { applyExportDecorators } from 'tabview/graphComponent/exportDecorators';
import {
  UrlFieldValuesPopover,
  type UrlFieldValuesPopoverHandle,
} from 'tabview/graphComponent/UrlFieldValuesPopover';
import { YFilesTooltipGlobalStyle } from 'tabview/graphComponent/YFilesTooltipGlobalStyle';

const VIEW_ID = ViewIds.MODERNIZED_BLOCK_DIAGRAM;
export const ZOOM_INCREMENT = 1.2;

const configureRendering = (
  graphComponent: GraphComponent,
  viewportLimiter: ViewportLimiter
) => {
  const inputMode = graphComponent.inputMode as GraphViewerInputMode;
  inputMode.navigationInputMode.enabled = false;
  inputMode.defaultCursor = Cursor.GRAB;
  graphComponent.contentMargins = 20;
  graphComponent.limitFitContentZoom = false;
  graphComponent.minimumZoom = 0.0000001;
  graphComponent.maximumZoom = 1000000;
  graphComponent.viewportLimiter = viewportLimiter;
  graphComponent.mouseWheelZoomFactor = ZOOM_INCREMENT;
  graphComponent.devicePixelRatio = devicePixelRatio;
  applyExportDecorators(graphComponent);
};

const MODERNIZED_BLOCK_DIAGRAM_HOVER_MODE = {
  edgeHover: true,
  highlightAdjacent: false,
  transparentizeNonAdjacent: true,
};

export type ModernizedBlockDiagramViewCoreHandle = {
  getGraphComponent: () => GraphComponent | null;
  zoomIn: () => void;
  zoomOut: () => void;
  fitContent: () => void;
  optimizeLayout: () => Promise<void>;
};

const ModernizedBlockDiagramViewCore = forwardRef<
  ModernizedBlockDiagramViewCoreHandle,
  ModernizedBlockDiagramViewCoreProps
>((props, ref) => {
  const graphComponentRef = useRef<GraphComponent | null>(null);
  const graphComponentContainerRef = useRef<HTMLDivElement | null>(null);
  const unregisterDragAndDrop = useRef(noop);
  const layoutPerformanceTrackingIdRef = useRef<string | null>(null);
  const previousLayoutDescriptor = useRef<LayoutDescriptor | null>(null);
  const [initialized, setInitialized] = useState(false);
  const urlFieldValuesPopoverRef = useRef<UrlFieldValuesPopoverHandle>(null);

  const graphBuilder = useRef<ModernizedYGraphBuilder | null>(null);
  const shouldClearSelection = useRef(false);
  const lastLayoutWasFresh = useRef(true);

  const getGraphComponent = useCallback(
    () => graphComponentRef.current ?? null,
    []
  );

  useEffect(() => {
    const graphComponent = getGraphComponent();
    if (shouldClearSelection.current && graphComponent) {
      shouldClearSelection.current = false;
      (graphComponent.inputMode as GraphInputMode).clearSelection();
    }
  });

  const {
    groups: viewModelGroups,
    edges: viewModelEdges,
    nodes: viewModelNodes,
    viewInstanceId,
    viewSettings,
    isViewpointMode,
    clearEdges,
    performanceTrackingId,
    viewStatusReporter = NOOP_VIEW_STATUS_REPORTER,
    dataWorkToken,
    loadedScenarioData,
    commands: {
      resetLayout,
      handleImageDrop,
      graphEventHandlers,
      recordRenderComplete,
    },
    expandedStateChangingGroupId,
    isPresentationMode,
    isAboveLimit,
    isEmpty,
    selectableItems = defaultSelectableItems,
    i18nLocale,
    bypassLimit = false,
    onWindowChanged,
    onWindowFitted,
    viewportLimiter = ModernizedViewportLimiter.Instance,
  } = props;
  const { componentStyle } = viewSettings;

  const graphViewDataModel: GraphViewDataModel<
    ComponentGraphNode,
    ComponentGraphGroup,
    GraphEdgeBase
  > = useMemo(
    () => ({
      groups: viewModelGroups,
      edges: viewModelEdges,
      nodes: viewModelNodes,
    }),
    [viewModelGroups, viewModelEdges, viewModelNodes]
  );

  const buildGraph = useCallback(
    (
      graphComponent: GraphComponent,
      bypassLimit: boolean,
      clearEdges: boolean
    ) => {
      const { graph } = graphComponent;

      graphBuilder.current ||= new ModernizedYGraphBuilder(graph);
      graphBuilder.current.componentStyle = componentStyle;
      graphBuilder.current.updateScenarioData(loadedScenarioData);

      if (clearEdges) {
        graphBuilder.current.clearEdges();
      }
      const groups = getAddedAndUpdatedItems(graphViewDataModel.groups);

      const nodes = getAddedAndUpdatedItems(graphViewDataModel.nodes);
      const edges = getAddedAndUpdatedItems(graphViewDataModel.edges);

      shouldClearSelection.current = isSelectionStaleAfterNodeUpdate(
        graphComponent,
        nodes
      );

      const hasLayoutUpdate = graphBuilder.current.hasLayoutUpdate({
        groups,
        nodes,
        edges,
      });

      if ((!bypassLimit && isAboveLimit) || isEmpty) {
        graphBuilder.current.groupsSource = [];
        graphBuilder.current.nodesSource = [];
        graphBuilder.current.edgesSource = [];
        graphBuilder.current.clear();
      } else {
        graphBuilder.current.groupsSource = groups;
        graphBuilder.current.nodesSource = nodes;
        graphBuilder.current.edgesSource = edges;
        graphBuilder.current.updateGraph();
      }

      return { hasLayoutUpdate };
    },
    [
      graphViewDataModel,
      componentStyle,
      loadedScenarioData,
      isAboveLimit,
      isEmpty,
    ]
  );

  const zoomIn = useCallback(
    () =>
      getGraphComponent()?.executeCommand(
        Command.INCREASE_ZOOM,
        ZOOM_INCREMENT
      ),
    [getGraphComponent]
  );
  const zoomOut = useCallback(
    () =>
      getGraphComponent()?.executeCommand(
        Command.DECREASE_ZOOM,
        ZOOM_INCREMENT
      ),
    [getGraphComponent]
  );
  const fitContent = useCallback(() => {
    getGraphComponent()?.fitContent();
  }, [getGraphComponent]);

  const optimizeLayout = useCallback(async () => {
    const graphComponent = getGraphComponent();

    // If the last layout was incremental (not fresh), do a full layout reset
    // to get optimal positioning after group expand/collapse operations
    if (!lastLayoutWasFresh.current) {
      resetLayout();
    } else if (graphComponent) {
      try {
        await graphComponent.fitContent(true);
      } catch {
        return;
      }
      onWindowFitted();
    }
  }, [getGraphComponent, resetLayout, onWindowFitted]);

  useImperativeHandle(
    ref,
    () => ({
      getGraphComponent,
      zoomIn,
      zoomOut,
      fitContent,
      optimizeLayout,
    }),
    [getGraphComponent, zoomIn, zoomOut, fitContent, optimizeLayout]
  );

  useEffect(() => {
    if (!graphComponentRef.current) {
      graphBuilder.current = null;
    }

    const currentGraphNodeCount = getGraphNodeCount(graphViewDataModel);

    if (!currentGraphNodeCount) {
      // normally isBusy=false is dispatched in the graph layout callback, but in this case we have no graph, so we must dispatch isBusy=false
      setTimeout(() =>
        dispatchAction(notifyViewLoading(viewInstanceId, { isBusy: false }))
      );
    }
  }, [viewInstanceId, graphViewDataModel]);

  useEffect(() => {
    const graphComponent = getGraphComponent();
    if (!graphComponent) {
      return;
    }
    graphComponent.addEventListener('viewport-changed', onWindowChanged);

    return () =>
      graphComponent.removeEventListener('viewport-changed', onWindowChanged);
  }, [getGraphComponent, initialized, onWindowChanged]);

  const onLayoutGraph = useCallback(
    (args: OnLayoutGraphArgs) => {
      onLayoutGraphHandler(expandedStateChangingGroupId)(args);
      const isLayoutFresh = isFreshLayout(args.layoutDescriptor);
      lastLayoutWasFresh.current = isLayoutFresh;
      if (isLayoutFresh) {
        queueMicrotask(onWindowFitted);
      }
    },
    [expandedStateChangingGroupId, onWindowFitted]
  );

  const { layoutOrientation, separateReferences } = viewSettings;
  const configureLayoutCallback = useCallback(
    (graphComponent: GraphComponent) =>
      configureLayout(
        graphComponent,
        {
          ...graphViewDataModel,
          expandedStateChangingGroupId,
        },
        {
          layoutOrientation,
          separateReferences,
        }
      ),
    [
      graphViewDataModel,
      expandedStateChangingGroupId,
      layoutOrientation,
      separateReferences,
    ]
  );

  const buildGraphCallback = useCallback(
    (graphComponent: GraphComponent) =>
      buildGraph(graphComponent, bypassLimit, clearEdges),
    [buildGraph, clearEdges, bypassLimit]
  );

  useEffect(
    // initialize graphComponent
    () => {
      if (!graphComponentContainerRef.current || graphComponentRef.current) {
        return;
      }
      graphComponentRef.current = createGraphComponent(
        graphComponentContainerRef.current,
        MODERNIZED_BLOCK_DIAGRAM_HOVER_MODE,
        popoverState =>
          urlFieldValuesPopoverRef.current?.setUrlFieldValuesPopover(
            popoverState
          )
      );

      unregisterDragAndDrop.current = initializeHighlightIndicator({
        graphComponent: graphComponentRef.current,
        viewInstanceId,
        handleImageDropCallback: handleImageDrop,
      });

      configureRendering(graphComponentRef.current, viewportLimiter);
      applySelectionDecorators(graphComponentRef.current, 25, 10, true);
      const graphComponent = graphComponentRef.current;
      (graphComponent.inputMode as GraphInputMode).addEventListener(
        GraphEventType.MULTI_SELECTION_FINISHED,
        () => updateNodesAndEdgesConnectednessToSelection(graphComponent)
      );
      addClickHandlers(graphComponentRef.current, {
        selectableItems,
        focusableItems,
        isPresentationMode,
        graphEventHandlers,
        isViewpointMode: () => isViewpointMode,
      });

      registerCanvasHitTest(viewInstanceId, {
        hitTest: toClientModelHitTest(graphComponentRef.current),
        locate: toClientNodeLocator(graphComponentRef.current),
      });

      setInitialized(true);

      return () => {
        unregisterDragAndDrop.current?.();
        unregisterCanvasHitTest(viewInstanceId);
      };
    },
    [
      isViewpointMode,
      isPresentationMode,
      handleImageDrop,
      graphEventHandlers,
      selectableItems,
      viewInstanceId,
      viewportLimiter,
    ]
  );

  useToolTipParentElement(graphComponentRef);

  useEffect(() => {
    const clearSelection = () => {
      const graphComponent = graphComponentRef.current;
      if (graphComponent) {
        (graphComponent.inputMode as GraphInputMode).clearSelection();
      }
    };
    const invalidateGraph = () => graphComponentRef.current?.invalidate();
    const subscriptions = [
      subscribeToAction(canvasResolvedImageReady, invalidateGraph),
      subscribeToAction(svgImageReady, invalidateGraph),
      subscribeToAction(clearGraphSelection, clearSelection),
    ];
    return () => {
      clearSubscriptions(subscriptions);
    };
  }, []);

  useEffect(() => {
    if (!initialized || !graphComponentRef.current) {
      return;
    }
    const { hasLayoutUpdate: hasGraphUpdate } = buildGraphCallback(
      graphComponentRef.current
    );
    const {
      layoutDescriptor,
      layoutData,
      duration,
      animateViewport,
      updateContentRect,
      targetBoundsInsets,
      portAdjustmentPolicy,
    } = configureLayoutCallback(graphComponentRef.current);
    if (
      graphComponentRef.current.graphModelManager instanceof
      FastGraphModelManager
    ) {
      graphComponentRef.current.graphModelManager.dirty = true;
    }
    graphComponentRef.current.invalidate();
    const isNewPerformanceTrackingId =
      performanceTrackingId !== layoutPerformanceTrackingIdRef.current;
    layoutPerformanceTrackingIdRef.current = performanceTrackingId;
    dispatchAction(notifyViewLoading(viewInstanceId, { isBusy: true }));
    runLayout({
      graphComponent: graphComponentRef.current,
      layoutDescriptor,
      layoutData,
      onLayoutGraph,
      performanceTrackingId: isNewPerformanceTrackingId
        ? performanceTrackingId
        : null,
      recordRenderComplete,
      hasGraphUpdate,
      duration,
      previousLayoutDescriptor: previousLayoutDescriptor.current,
      animateViewport,
      updateContentRect,
      targetBoundsInsets,
      portAdjustmentPolicy,
      viewInstanceId,
      viewId: VIEW_ID,
      viewStatusReporter,
    });
    previousLayoutDescriptor.current = layoutDescriptor;
  }, [
    initialized,
    buildGraphCallback,
    configureLayoutCallback,
    onLayoutGraph,
    performanceTrackingId,
    recordRenderComplete,
    viewInstanceId,
    viewStatusReporter,
  ]);

  useEffect(() => {
    if (initialized && dataWorkToken) {
      viewStatusReporter.reportWorkFinished(dataWorkToken);
    }
  }, [initialized, dataWorkToken, viewStatusReporter]);

  return (
    <FillingDiv>
      {/* The tooltip yFiles renders is mounted on `document.body`, so its styling cannot live in
          a styled-component here - see `YFilesTooltipGlobalStyle`. */}
      <YFilesTooltipGlobalStyle />
      {/* supporting UI should be rendered in the parent;
          the single purpose of this component is interfacing the yfiles graph component */}
      <GraphContainer
        $isModern={true}
        className="yfiles-canvascomponent"
        ref={graphComponentContainerRef}
        id="graphComponent"
        role="img"
        aria-label={t(
          i18nLocale,
          'tabview:blockDiagram.graph.ariaLabel',
          'Visualization of the graph'
        )}
        data-context-menu={OBJECT_CONTEXT_MENU_NAME}
        data-canvas-hit-test={viewInstanceId}
      />
      <UrlFieldValuesPopover ref={urlFieldValuesPopoverRef} viewId={VIEW_ID} />
    </FillingDiv>
  );
});

ModernizedBlockDiagramViewCore.displayName = 'ModernizedBlockDiagramViewCore';

export { ModernizedBlockDiagramViewCore };
